'use client';

/**
 * Tap-to-install link for phones — the mobile stand-in for the app QR code
 * (a QR is no use to someone already holding the phone).
 *
 * With the server HTML (and with JS off) it points at /get-app, which sends
 * each phone to its store. Once mounted: iOS → App Store; Android on a
 * campsite → intent:// that opens the installed app on that campsite, or
 * Play when the app is missing.
 */
import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { withBase } from '../lib/brand';
import { detectDevice, reportAppGetClick } from '../lib/appAnalytics';
import {
  androidOpenCampsiteUrl,
  appStoreUrl,
  campsiteSlugFromPath,
  playStoreUrl,
} from '../lib/appInstall';

export type InstallDevice = 'ios' | 'android' | 'desktop' | null;

export function useInstallLink(source: string) {
  const slug = campsiteSlugFromPath(usePathname() ?? '');
  const [device, setDevice] = useState<InstallDevice>(null);

  useEffect(() => {
    setDevice(
      detectDevice(navigator.userAgent || '', navigator.platform || '', navigator.maxTouchPoints || 0),
    );
  }, []);

  const href =
    device === 'ios'
      ? appStoreUrl(source)
      : device === 'android'
        ? slug
          ? androidOpenCampsiteUrl(slug, source)
          : playStoreUrl(source, null)
        : withBase(`/get-app?s=${source}`);

  const opensApp = device === 'android' && !!slug;
  const onClick = () =>
    reportAppGetClick(device === 'ios' ? 'app-store' : device === 'android' ? 'play-store' : 'lander', source);

  return { href, device, slug, opensApp, onClick };
}
